/**
 * Buyer reviews for the home page: screenshots from Kanata's marketplace
 * store, shown as they are because Kanata wants the proof to look like the
 * real thing.
 *
 * Each screenshot is cropped to the review itself (stars through the buyer's
 * photos, without the marketplace's buttons). Wide ones are unreadable at
 * phone width, so each also has a `-m` version at most 440px wide: the same
 * screenshot pixels with the words cut out and re-wrapped onto shorter lines
 * (scripts/review-screenshots). Nothing is retouched.
 *
 * The text is also kept here, as typed from the screenshot with masking and
 * typos intact, so the image has an honest alt text. Ages ("2 minggu lalu")
 * are as of 2026-10-06.
 */

export interface Review {
  name: string;
  text: string;
  /** The cropped screenshot, and its re-wrapped phone version. */
  image: Shot;
  mobile: Shot;
}

export interface Shot {
  src: string;
  width: number;
  height: number;
}

const screenshots = import.meta.glob<string>("@/images/reviews/*.webp", {
  eager: true,
  import: "default",
});

/** Pixel sizes: [desktop width, height, phone width, height]. */
const sizes: Record<number, [number, number, number, number]> = {
  1: [709, 346, 433, 413],
  2: [1176, 386, 438, 497],
  3: [1185, 386, 419, 537],
  4: [1181, 386, 393, 538],
  5: [1192, 377, 379, 521],
  6: [777, 346, 390, 414],
  7: [664, 325, 408, 385],
  8: [1344, 386, 406, 500],
  9: [1080, 346, 415, 419],
  10: [1329, 386, 439, 703],
  11: [717, 346, 428, 376],
  12: [1290, 386, 439, 459],
  13: [479, 346, 373, 375],
  14: [1296, 386, 440, 527],
  15: [383, 197, 378, 184],
  16: [1331, 386, 440, 574],
};

/** Screenshot `n` (review-NN.webp, numbered in the order they were taken). */
function shot(n: number): Pick<Review, "image" | "mobile"> {
  const file = (suffix: string) => {
    const name = `/review-${String(n).padStart(2, "0")}${suffix}.webp`;
    const key = Object.keys(screenshots).find((path) => path.endsWith(name));
    return key ? screenshots[key]! : "";
  };
  const [w = 0, h = 0, mw = 0, mh = 0] = sizes[n] ?? [];
  return {
    image: { src: file(""), width: w, height: h },
    mobile: { src: file("-m"), width: mw, height: mh },
  };
}

// Ordered so the carousel opens on the most specific praise. The two reviews
// with a criticism stay in: they are part of what buyers said.
export const reviews: Review[] = [
  {
    name: "Brother",
    text: "sangat puas dengan kualitas dari produk nya. semoga awet hingga 3-5 tahun dan akhirnya bisa menggapai setup impian saya dari tahun lalu. Thank you, Kana****ndonesia 😜🌹",
    ...shot(14),
  },
  {
    name: "Fritz",
    text: "Meja nya bagus, rangka dan daun mejanya tebal, motor penggerak berfungsi normal, puas dengan pelayanannya juga",
    ...shot(8),
  },
  {
    name: "Muhammad",
    text: "Barang sesuai, kualitas bahan solid metal sesuai harga, udah lengkap dengan baut nya juga 2. Packaging rapi dengan bubble wrap",
    ...shot(3),
  },
  {
    name: "Fadly",
    text: "Barang bagus sesuai deskripsi. Meja koko****dak goyang juga. Daun meja juga solid dan berat. Pengiriman cepat",
    ...shot(12),
  },
  {
    name: "T***r",
    text: "paket aman sekali gaada kendala apapun, tanpa packing kayu juga untuk pengiriman daerah depok... Overall saya puas 😁😁 tapi menguras energi untuk rakitnya, 3 jam ngera…",
    ...shot(10),
  },
  {
    name: "Agung",
    text: "Untuk mejanya mntp banget dan berfungsi dengan baik, untuk perakitannya saya berdua, itu perlu waktu dan sedikit kesabaran biar hasilnya mau bagus, untuk adminya juga res…",
    ...shot(16),
  },
  {
    name: "A***l",
    text: "barang sesuai pesanan, dan kualitas nya kokoh. pelayanan seller juga bagus 👌🏼",
    ...shot(9),
  },
  {
    name: "A***a",
    text: "barang yang datang sesuai dengan pesanan, kwalitas produk oke, soket penyimpanan kabel lengkap dengan mur dan bautnya..",
    ...shot(4),
  },
  {
    name: "Fadly",
    text: "Bagus, udah jadi pas sam*******oh dan tidak goyang",
    ...shot(11),
  },
  {
    name: "E***y",
    text: "Keren, ternyata cukup besar juga cable managementnya",
    ...shot(6),
  },
  {
    name: "Fadly",
    text: "Bagus barangnya sesuai deskripsi",
    ...shot(13),
  },
  {
    name: "Owen",
    text: "barang sesuai deskripsi trims gan sukses selalu",
    ...shot(7),
  },
  {
    name: "E***a",
    text: "Kayaknya lebih oke harus ditambah bautnya, jadi ga cuma 2 karena plat besinya berat. Biar lebih kokoh aja!",
    ...shot(2),
  },
  {
    name: "G***y",
    text: "Metrial bagus, tebal, tapi penempatannya agak aneh menurut saya karena harus dibaut juga yang bikin meja bolong",
    ...shot(5),
  },
  {
    name: "Razi",
    text: "Paket Sudah Sampai Dengan Selamat Terima kasih.",
    ...shot(1),
  },
  {
    name: "Dimas",
    text: "Barang bagus",
    ...shot(15),
  },
];
